import { useState } from 'react';
import { Button, Card, Col, InputNumber, message, Modal, Row, Select, Table, Tag, Typography } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, ShoppingCartOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useTrading } from '../context/TradingContext';
import type { Quote } from '../api/market';

const { Title, Text } = Typography;
const SECTORS = ['All', 'Technology', 'Finance', 'Healthcare', 'Consumer', 'Energy', 'Media', 'Retail', 'Automotive'];

export default function Market() {
  const { quotes, portfolio, buy, sell } = useTrading();
  const [sector, setSector] = useState('All');
  const [tradeModal, setTradeModal] = useState<{ stock: Quote; side: 'buy' | 'sell' } | null>(null);
  const [shares, setShares] = useState(1);
  const [loading, setLoading] = useState(false);

  const filtered = sector === 'All' ? quotes : quotes.filter(q => q.sector === sector);

  function openTrade(stock: Quote, side: 'buy' | 'sell') {
    setShares(1);
    setTradeModal({ stock, side });
  }

  async function executeTrade() {
    if (!tradeModal || !shares || shares < 1) return;
    setLoading(true);
    const err = tradeModal.side === 'buy'
      ? buy(tradeModal.stock.symbol, shares)
      : sell(tradeModal.stock.symbol, shares);
    setLoading(false);
    if (err) { message.error(err); }
    else {
      message.success(`${tradeModal.side === 'buy' ? 'Bought' : 'Sold'} ${shares} shares of ${tradeModal.stock.symbol}`);
      setTradeModal(null);
    }
  }

  const columns = [
    { title: 'Symbol', dataIndex: 'symbol', key: 'symbol',
      render: (s: string) => <strong style={{ fontFamily: 'monospace', fontSize: 14 }}>{s}</strong> },
    { title: 'Company', dataIndex: 'name', key: 'name' },
    { title: 'Sector', dataIndex: 'sector', key: 'sector',
      render: (s: string) => <Tag>{s}</Tag> },
    { title: 'Price', dataIndex: 'price', key: 'price', align: 'right' as const,
      render: (v: number) => <strong>${v.toFixed(2)}</strong> },
    { title: 'Change', dataIndex: 'changePct', key: 'changePct', align: 'right' as const,
      render: (v: number, row: Quote) => (
        <span style={{ color: v >= 0 ? '#52c41a' : '#ff4d4f', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
          {v >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
          {v >= 0 ? '+' : ''}{v.toFixed(2)}% (${row.change >= 0 ? '+' : ''}{row.change.toFixed(2)})
        </span>
      ) },
    { title: 'Your Shares', key: 'holding', align: 'right' as const,
      render: (_: unknown, row: Quote) => {
        const pos = portfolio.positions.find(p => p.symbol === row.symbol);
        return pos ? <Text type="success">{pos.shares}</Text> : <Text type="secondary">—</Text>;
      } },
    { title: 'Actions', key: 'actions',
      render: (_: unknown, row: Quote) => {
        const pos = portfolio.positions.find(p => p.symbol === row.symbol);
        return (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="small" type="primary" icon={<ShoppingCartOutlined />}
              onClick={() => openTrade(row, 'buy')}>Buy</Button>
            {pos && (
              <Button size="small" danger icon={<MinusCircleOutlined />}
                onClick={() => openTrade(row, 'sell')}>Sell</Button>
            )}
          </div>
        );
      } },
  ];

  const totalCost = tradeModal ? +(tradeModal.stock.price * (shares || 0)).toFixed(2) : 0;
  const canAfford = tradeModal?.side === 'buy' ? totalCost <= portfolio.cash : true;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Market</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Text type="secondary">Cash: <strong>${portfolio.cash.toLocaleString('en', { minimumFractionDigits: 2 })}</strong></Text>
          <Select value={sector} onChange={setSector} style={{ width: 150 }} size="small">
            {SECTORS.map(s => <Select.Option key={s} value={s}>{s}</Select.Option>)}
          </Select>
        </div>
      </div>

      <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
        {quotes.filter(q => q.changePct > 0).sort((a, b) => b.changePct - a.changePct).slice(0, 3).map(q => (
          <Col key={q.symbol} xs={8} sm={4}>
            <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{q.symbol}</div>
              <div style={{ color: '#52c41a', fontSize: 12 }}>+{q.changePct.toFixed(2)}%</div>
            </Card>
          </Col>
        ))}
        {quotes.filter(q => q.changePct < 0).sort((a, b) => a.changePct - b.changePct).slice(0, 3).map(q => (
          <Col key={q.symbol} xs={8} sm={4}>
            <Card size="small" style={{ background: '#fff2f0', borderColor: '#ffa39e' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{q.symbol}</div>
              <div style={{ color: '#ff4d4f', fontSize: 12 }}>{q.changePct.toFixed(2)}%</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="symbol"
          size="small"
          pagination={{ pageSize: 15 }}
        />
      </Card>

      <Modal
        title={tradeModal ? `${tradeModal.side === 'buy' ? 'Buy' : 'Sell'} ${tradeModal.stock.symbol}` : ''}
        open={!!tradeModal}
        onCancel={() => setTradeModal(null)}
        onOk={executeTrade}
        okText={tradeModal?.side === 'buy' ? 'Buy' : 'Sell'}
        okButtonProps={{ danger: tradeModal?.side === 'sell', loading, disabled: !canAfford || !shares || shares < 1 }}
        confirmLoading={loading}
      >
        {tradeModal && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>{tradeModal.stock.name}</Text>
              <Text strong>${tradeModal.stock.price.toFixed(2)}</Text>
            </div>
            {tradeModal.side === 'sell' && (
              <Text type="secondary">
                You hold {portfolio.positions.find(p => p.symbol === tradeModal.stock.symbol)?.shares ?? 0} shares
              </Text>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Text>Shares:</Text>
              <InputNumber min={1} max={tradeModal.side === 'sell'
                ? portfolio.positions.find(p => p.symbol === tradeModal.stock.symbol)?.shares ?? 1
                : 10000}
                value={shares} onChange={v => setShares(v ?? 1)} style={{ flex: 1 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid #f0f0f0' }}>
              <Text>Estimated Total:</Text>
              <Text strong style={{ color: canAfford ? undefined : '#ff4d4f' }}>
                ${totalCost.toLocaleString('en', { minimumFractionDigits: 2 })}
              </Text>
            </div>
            {tradeModal.side === 'buy' && !canAfford && (
              <Text type="danger">Insufficient funds. Available: ${portfolio.cash.toFixed(2)}</Text>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
